import React, { createContext, useContext } from 'react'

/**
 * The name of the role the user is acting as.
 *
 * It has to be carried separately because the expense views are told about every role *except*
 * this one: the backend sends `other_roles`, and the acting role reaches the client as the literal
 * `'myself'` — for the responsible field, resolved server-side (`map_to_myself`). That is the right
 * wire protocol, since "the acting role" is exactly what a client should be allowed to say without
 * knowing its own role id. It is the wrong thing to *show*: a list then reads "Kassör, mig själv",
 * mixing a name with a pronoun, and in the objective form at that.
 *
 * So the protocol keeps `'myself'` and the interface says which role that is. A context rather than
 * four levels of prop, as with the scope and the capabilities beside it.
 */
const ActiveRoleContext = createContext<string | null>(null)

export function ActiveRoleProvider({ name, children }: {
    name: string | null,
    children: React.ReactNode,
}) {
    return <ActiveRoleContext.Provider value={name}>{children}</ActiveRoleContext.Provider>
}

/** The acting role's name, or null before the roles have loaded. */
export function useActiveRoleName(): string | null {
    return useContext(ActiveRoleContext)
}

/**
 * How to write the acting role in a list of roles: its name, marked as the reader's own.
 *
 * Falls back to the pronoun when the name is unknown, which is better than an empty parenthesis.
 */
export function selfLabel(name: string | null): string {
    return name === null ? 'du själv' : `${name} (du själv)`
}
